// UNIVAPLAT-1921: New Message modal — template dropdown, variable slots, live preview.
// UNIVAPLAT-1955: auto-resolved variables (e.g. first_name) are hidden from the admin
//   and substituted with %NAME% tokens in the preview; excluded from the BE payload.
// UNIVAPLAT-2550: bulk mode — when opened from the admin-dashboard "Bulk actions" entry
//   point the modal composes the same template/variables but confirms through
//   AdminWhatsappBulkConfirmModal, carrying the dashboard filter payload instead of a userId.
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {debounceTime, Subscription} from 'rxjs';
import {AdminWhatsappService} from '@shared/api/services/v2/admin-whatsapp.service';
import {WhatsAppTemplate, WhatsAppVariable} from '@shared/api/models/query/admin-whatsapp.model';
import {AdminDashboardFilters} from '@shared/api/models/query/admin-user.model';
import {AdminWhatsappConfirmModal} from './admin-whatsapp-confirm.modal';
import {AdminWhatsappBulkConfirmModal} from './admin-whatsapp-bulk-confirm.modal';

@Component({
    selector: 'cdss-admin-whatsapp-new-message-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
    templateUrl: './admin-whatsapp-new-message.modal.html',
    styleUrls: ['./admin-whatsapp-new-message.modal.scss'],
})
export class AdminWhatsappNewMessageModal implements OnInit, OnDestroy {
    /** Single-send mode: target user. Not set (and not read) in bulk mode. */
    @Input() userId!: number;
    @Input() maskedPhone: string = '';

    // UNIVAPLAT-2550: bulk mode inputs — set by the admin-dashboard "Bulk actions" entry point.
    /** When true, confirmation goes through AdminWhatsappBulkConfirmModal with `bulkFilters`. */
    @Input() bulkMode = false;
    /** Admin-dashboard filter payload the backend re-resolves recipients from. Required when bulkMode. */
    @Input() bulkFilters: AdminDashboardFilters | null = null;
    /** Recipient count currently shown on the dashboard (pagination.totalCount). Display-only. */
    @Input() bulkRecipientCount = 0;

    loadingTemplates = true;
    templateError: string | null = null;
    templates: WhatsAppTemplate[] = [];
    selectedTemplate: WhatsAppTemplate | null = null;
    previewBody = '';

    /**
     * UNIVAPLAT-1955: parallel metadata array aligned to `variablesArray` controls.
     * Only non-auto-resolved variables get a FormControl pushed; this array records
     * the original WhatsAppVariable for every such control so the template can
     * render labels and rebuildPreview can map control indexes back to positions.
     */
    variableMeta: WhatsAppVariable[] = [];

    form!: FormGroup;
    private previewSub?: Subscription;

    constructor(
        public modal: NgbActiveModal,
        private adminWhatsappService: AdminWhatsappService,
        private modalService: NgbModal,
        private fb: FormBuilder,
    ) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            templateSid: [null, Validators.required],
            variables: this.fb.array([]),
        });
        this.loadTemplates();
        this.setupPreview();
    }

    get variablesArray(): FormArray { return this.form.get('variables') as FormArray; }

    async loadTemplates(): Promise<void> {
        this.loadingTemplates = true;
        this.templateError = null;
        try {
            const resp = await this.adminWhatsappService.getTemplates();
            this.templates = resp.templates ?? [];
        } catch (err: any) {
            this.templateError = err?.error?.message ?? err?.message ?? 'Failed to load templates.';
        } finally {
            this.loadingTemplates = false;
        }
    }

    onTemplateSelect(template: WhatsAppTemplate | null): void {
        this.selectedTemplate = template ?? null;
        this.variablesArray.clear();
        this.variableMeta = [];
        if (template) {
            // UNIVAPLAT-1955: only push controls for non-auto-resolved variables.
            // Auto-resolved ones (e.g. first_name) are substituted by the BE and
            // shown as %NAME% tokens in the preview — no admin input required.
            template.variables.forEach(v => {
                if (!v.autoResolved) {
                    this.variableMeta.push(v);
                    this.variablesArray.push(new FormControl('', Validators.required));
                }
            });
        }
        this.rebuildPreview();
    }

    private setupPreview(): void {
        this.previewSub = this.form.valueChanges.pipe(debounceTime(50)).subscribe(() => this.rebuildPreview());
    }

    rebuildPreview(): void {
        if (!this.selectedTemplate?.body) { this.previewBody = ''; return; }
        let body = this.selectedTemplate.body;
        // UNIVAPLAT-1955: iterate all template variables by token.
        // Auto-resolved → substitute with %NAME% token; admin-entered → use typed value.
        const adminVals: string[] = this.variablesArray.value;
        let adminIdx = 0;
        this.selectedTemplate.variables.forEach(v => {
            const placeholder = new RegExp(`\\{\\{${v.token}\\}\\}`, 'g');
            if (v.autoResolved) {
                body = body.replace(placeholder, `%${v.name.toUpperCase()}%`);
            } else {
                const typed = adminVals[adminIdx] || `{{${v.token}}}`;
                body = body.replace(placeholder, typed);
                adminIdx++;
            }
        });
        this.previewBody = body;
    }

    variableLabel(i: number): string {
        const v = this.variableMeta[i];
        return v?.name ? v.name : `Variable {{${(v?.token ?? i + 1)}}}`;
    }

    /** UNIVAPLAT-2550: value of the read-only "To:" field — recipient summary in bulk mode. */
    get toFieldValue(): string {
        return this.bulkMode
            ? `${this.bulkRecipientCount} user(s) matching current dashboard filters`
            : this.maskedPhone;
    }

    openConfirmModal(): void {
        // UNIVAPLAT-1955: exclude auto-resolved tokens from the BE payload.
        const vars: Record<string, string> = {};
        (this.variablesArray.value as string[]).forEach((val, idx) => {
            const token = this.variableMeta[idx]?.token ?? String(idx + 1);
            vars[token] = val;
        });
        // Default (medium) size: the confirmation is now a short yes/no question, and the
        // message preview it used to carry lives in this modal instead.
        if (this.bulkMode) {
            // UNIVAPLAT-2534: fail closed — a null `bulkFilters` must never reach the bulk
            // confirm modal. An empty filter payload would silently degrade the audience to
            // role='patient,carer' with NO status filter (drafts included), i.e. broader than
            // any dashboard preview ever showed. Unreachable via the only current entry point
            // (AdminDashboardPage.openBulkWhatsAppSend() always passes buildFilters()), but a
            // future caller's programming error must be a no-op here, not a mass send.
            if (!this.bulkFilters) { return; }
            // UNIVAPLAT-2550: bulk confirmation — carries the filter payload, never a user list.
            const ref = this.modalService.open(AdminWhatsappBulkConfirmModal, {centered: true});
            ref.componentInstance.filters = this.bulkFilters;
            ref.componentInstance.templateSid = this.selectedTemplate!.templateSid;
            ref.componentInstance.variableValues = vars;
            ref.componentInstance.recipientCount = this.bulkRecipientCount;
            ref.result.then(result => {
                if (result === 'refresh') { this.modal.close('refresh'); }
                // 'back' means stay open with values intact — do nothing
            }).catch(() => {/* dismissed — stay open */});
            return;
        }
        const ref = this.modalService.open(AdminWhatsappConfirmModal, {centered: true});
        ref.componentInstance.userId = this.userId;
        ref.componentInstance.templateSid = this.selectedTemplate!.templateSid;
        ref.componentInstance.variableValues = vars;
        ref.result.then(result => {
            if (result === 'refresh') { this.modal.close('refresh'); }
            // 'back' means stay open with values intact — do nothing
        }).catch(() => {/* dismissed — stay open */});
    }

    get isSendDisabled(): boolean {
        return this.form.invalid || this.loadingTemplates || !!this.templateError;
    }

    cancel(): void { this.modal.dismiss(); }

    ngOnDestroy(): void { this.previewSub?.unsubscribe(); }
}
